import './Global.scss';

/* eslint-disable max-lines-per-function */
import {MenuBar, SkipToSection} from '@mvps-genesis/accessibility';
import {PageManager} from '@mvps-genesis/bootstrap';
import CookieJar from '@mvps-genesis/cookie-jar';
import 'construct-style-sheets-polyfill';
import 'custom-extensions/g-modal';
import 'custom-extensions/ui-components/x-product-card';
import ConfirmAction from 'extensions/confirm-action';
import ContinueShopping from 'extensions/continue-shopping';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
import {GlobalMessageContainer} from 'extensions/messages';
import MiniBasket from 'extensions/mini-basket';
import NavigationMenu from 'extensions/navigation-menu';
import 'custom-extensions/free-shipping-bar/lib/FreeShippingBar';

export default class Global extends PageManager {

	cookieNames = {
		accountPreferencesPopup: 'accountPreferencesPopup',
		ageGate: 'ageCookie',
		globalPopups: 'globalPopups'
	};

	// eslint-disable-next-line max-lines-per-function
	constructor (pageContext) {
		super(pageContext);

		this.ageCookie = pageContext?.ageGateActive && !pageContext?.ByPassAgeGate ? CookieJar.getCookie(this.cookieNames.ageGate) : 'true';

		this.initAgeGateModal();
	}

	// eslint-disable-next-line max-lines-per-function
	onReady () {

		/**
		 * Initialize the Navigation Menu extension
		 */
		const mainMenuElem = document.querySelector('.js-navigation-menu');
		if (mainMenuElem) {
			new NavigationMenu({
				drawers: '.js-navigation-menu__drawer',
				mainMenu: mainMenuElem,
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous'
			});
		}

		const accountLinks = document.getElementById('js-customer-account-links');
		if (accountLinks) {
			// Basic instance constructor
			new MenuBar({
				breakpoint: false,
				element: accountLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		const staticLinks = document.getElementById('js-static-page-links');
		if (staticLinks) {
			// Basic instance constructor
			new MenuBar({
				element: staticLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		/**
		 * Initialize the Mini-Basket extension
		 */
		if (document.querySelector('.js-mini-basket')) {
			new MiniBasket({
				openOnAddToCart: false
			});
		}

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
		this.triggerPrint();
		this.initCollapsibleMenu();

		new ConfirmAction();

	}

	/*
	 * Determine if user has accepted they are over 18.
	 */
	initAgeGateModal () {
		// If user has accepted age verification from incoming landing page, set the cookie, and allow them to continue	through the site.
		const urlParams = new URLSearchParams(window.location.search);
		const paramValue = urlParams.get('age');

		if (paramValue === 'verified') {
			CookieJar.setCookie(this.cookieNames.ageGate, 'true');
		}

		// If user has accepted, then allow them to continue through the site.
		if (this.ageCookie === 'true') {
			this.onAfterAgeGate();
			return;
		}

		const modalElem = document.getElementById('warning-modal');
		if (!modalElem) {
			this.onAfterAgeGate();
			return;
		}

		// Display the modal
		modalElem.style.display = 'block';
		document.body.style.overflowY = 'hidden';

		// If user accepts, set the cookie, hide the modal.
		const modalButtonAccept = modalElem.querySelector('#adult_site_stay');
		modalButtonAccept?.addEventListener?.('click', () => {
			CookieJar.setCookie(this.cookieNames.ageGate, 'true');

			document.body.classList.remove('age-gate-content-hidden');

			modalElem.style.display = 'none';
			document.body.style.removeProperty('overflow-y');

			this.onAfterAgeGate();
		});

		// If user Declines, send them to Google
		const modalButtonDecline = modalElem.querySelector('#adult_site_leave');
		modalButtonDecline?.addEventListener?.('click', () => {
			window.location.replace('https://www.google.com');
		});

		if (window.location.href.includes('/blog/privacy-policy/')) {
			document.body.classList.remove('age-gate-content-hidden');

			modalElem.style.display = 'none';
			document.body.style.removeProperty('overflow-y');

			this.onAfterAgeGate();
		}
	}

	/*
	 * Functions to run after successful Age Gate requirement (if applicable)
	 */
	async onAfterAgeGate () {
		this.initSkipToSection();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});

		/**
		 * Initialize carousels
		 */
		this.initCarousels();

		this.initializeHTMLFormValidator();
		this.initializeSubmitEvents();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.initModals();
		this.initLazyload();
		this.initCookieConsent();
		this.initAddressEvents();
		this.initScrollTo();
		this.initBackToTop();
		this.initStickyListener();
		this.initAjaxLoginRegister();
		this.initFormLoadingUI();
		this.initTabbedCarousels();
		this.initGTM();
		this.initGlobalNewsletter();

		this.urlParameterPromotion();
		this.initCampaignLanding();
		this.globalSlimBanners();
		this.initComponents();
		this.initKlevu();

		// If this is true, don't do the global popups. We don't want more than 2 pop ups.
		if (await this.initAccountPreferencesModal()) {
			return;
		}

		this.initGlobalPopups();
	}

	initSkipToSection () {
		const sections = [];

		const menuElem = document.querySelector('.js-navigation-menu');

		if (menuElem) {
			sections.push({
				label: 'Menu',
				targetElement: menuElem
			});
		}

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const facetsElem = document.getElementById('js-facets');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		sections.push({
			label: 'Content',
			targetElement: '.g-main-content-element'
		});

		sections.push({
			label: 'Footer',
			targetElement: '.g-site-footer'
		});

		SkipToSection.createFromArray(sections);
	}

	async initLazyload () {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		// Module
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes');
	}

	async initCookieConsent () {
		const cookieName = `mm5-${this.pageContext.Store_Code}-genesis-cookie-consent`;

		if (document.cookie.indexOf(cookieName) !== -1) {
			return;
		}

		const {CookieConsent} = await import('extensions/cookie-consent');

		let contentLink = 'https://www.cookiesandyou.com/';
		let contentLinkTarget = '_blank';

		if (this.pageContext.Privacy_Policy_Url) {
			contentLink = this.pageContext.Privacy_Policy_Url;
			contentLinkTarget = '';
		}

		new CookieConsent({
			content: {
				dismiss: 'Got it!',
				href: contentLink,
				link: 'Learn More',
				message: 'This website uses cookies to ensure you get the best experience on our website.',
				target: contentLinkTarget
			},
			cookie: {
				name: cookieName,
				secure: true
			},
			palette: {
				button: {
					background: '#F9F9F9',
					text: '#000000'
				},
				popup: {
					background: '#222222',
					text: '#FFFFFF'
				}
			}
		});
	}

	async initCarousels () {
		const carouselElements = document.getElementsByClassName('js-carousel');
		if (!carouselElements) {
			return;
		}

		const {initCarousels} = await import('extensions/carousel');
		initCarousels();
	}

	async initializeHTMLFormValidator () {
		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const form of forms) {
			form.HTMLFormValidator = new HTMLFormValidator({
				fieldContainer: '.g-form-list__item',
				formElement: form
			});
		}
	}

	async initializeSubmitEvents () {
		const forms = document.getElementsByClassName('js-submit-loader');

		if (forms.length === 0) {
			return;
		}

		const {FormSubmitEvents} = await import('extensions/form-submit-events');

		for (const form of forms) {
			form.submitLoaderEvent = new FormSubmitEvents({
				formElement: form
			});
		}
	}

	async initModals () {
		const hasModalTrigger = document.querySelector(`[data-micromodal-trigger]`);

		if (!hasModalTrigger) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal();

		/**
		 * Initialize modals:
		 * 	Pass list of modal ids without an overlay that should close when
		 * 	clicking off of modal.
		 */
		modal.init([
			'js-search-form',
			'js-global-account'
		]);
	}

	async initAddressEvents () {
		const datalists = document.querySelectorAll('[data-address-datalist]');
		const toggles = document.querySelectorAll('[data-address-toggle]');

		if (datalists.length === 0 && toggles.length === 0) {
			return;
		}

		const {AddressEvents} = await import('extensions/address-events');

		new AddressEvents(datalists, toggles);
	}

	triggerPrint () {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			for (const element of printTriggers) {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			}
		}

	}

	async initScrollTo () {
		const triggerElements = document.getElementsByClassName('js-scroll-to');
		if (!triggerElements.length) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		ScrollTo.createFromNodeList(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest'
			}
		});

	}

	async initBackToTop () {

		const backToTopButton = document.getElementById('js-back-to-top-button');

		if (!backToTopButton) {
			return;
		}

		const backToTopClass = 'g-back-to-top-button--shown';

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo('#js-back-to-top-button', {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				inline: 'nearest'
			}
		});

		let lastScrollTop = 0;

		window.addEventListener('scroll', () => {
			this.showHideBackToTop(backToTopButton, backToTopClass);

			// Detect if user is scrolling up or down
			let st = window.pageYOffset || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				document.body.classList.remove('g-user-scrolling-up');
			} else {
				document.body.classList.add('g-user-scrolling-up');
			}

			lastScrollTop = st <= 0 ? 0 : st;
		});

		this.showHideBackToTop(backToTopButton, backToTopClass);

	}

	showHideBackToTop (backToTopButton, backToTopClass) {

		const heightToShowButton = window.innerHeight;
		const offset = heightToShowButton / 2;
		const windowOffset = offset + (isNaN(window.pageYOffset) ? 0 : window.pageYOffset);

		if (windowOffset >= heightToShowButton) {
			backToTopButton.classList.add(backToTopClass);
		} else {
			backToTopButton.classList.remove(backToTopClass);
		}

	}

	initStickyListener () {
		if (!('IntersectionObserver' in window)) {
			return;
		}

		const header = document.querySelector('.g-site-header:not(.g-site-header__unsticky)');

		if (!header) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => e.target.classList.toggle('g-site-header__sticky', e.intersectionRatio < 1),
			{threshold: [1]}
		);

		observer.observe(header);
	}

	async initAjaxLoginRegister () {
		const loginRegisterElement = document.getElementById('js-login-register');

		if (!loginRegisterElement) {
			return;
		}

		const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

		new AjaxLoginRegister(loginRegisterElement);
	}

	async initFormLoadingUI () {
		const formElements = document.getElementsByClassName('js-form-show-loading');

		if (!formElements.length) {
			return;
		}

		const {default: LoadingUI} = await import('extensions/loading-ui');
		const loadingContainerSelector = '.js-loading-container';
		const loadingElementSelector = '.js-loading-element';

		for (const formElement of formElements) {
			const loadingContainerElement = formElement.closest(loadingContainerSelector);

			if (!loadingContainerElement) {
				continue;
			}

			const loadingElement = loadingContainerElement.querySelector(loadingElementSelector);

			if (!loadingElement) {
				continue;
			}

			formElement.LoadingUI = new LoadingUI(loadingElement, {
				overlayElement: loadingContainerElement.hasAttribute('data-disable-loading-overlay') ? false : loadingContainerElement
			});

			formElement.addEventListener('submit', () => {
				formElement.LoadingUI.show();
			});
		}
	}

	async initCollapsibleMenu () {

		const menuElements = document.getElementsByClassName('js-collapsible-menu');

		if (menuElements.length === 0) {
			return;
		}

		const {default: CollapsibleMenu} = await import('extensions/collapsible-menu');

		new CollapsibleMenu(menuElements);

	}

	async initTabbedCarousels () {

		const tabbedCarouselElements = document.getElementsByClassName('js-tabbed-carousel');

		if (!tabbedCarouselElements.length) {
			return;
		}

		const {default: TabbedCarousels} = await import('extensions/tabbed-carousels');

		new TabbedCarousels(tabbedCarouselElements);

	}

	async initGTM () {

		const {default: GTM} = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}

	async initGlobalNewsletter () {
		const {default: NewsletterSignUp} = await import('custom-extensions/newsletter-signup');

		new NewsletterSignUp(this.pageContext);
	}

	/*
	 * Show Account Preferences Modal if applicable
	 */
	async initAccountPreferencesModal () {
		const accountPreferencesPopupCookie = CookieJar.getCookie(this.cookieNames.accountPreferencesPopup);
		if (accountPreferencesPopupCookie === 'true') {
			return false;
		}

		if (!document.getElementById('nsp-prompt')) {
			return false;
		}

		await import('micromodal');

		window.MicroModal.show('nsp-prompt');

		CookieJar.setCookie(this.cookieNames.accountPreferencesPopup, 'true');

		return true;
	}

	async initGlobalPopups () {
		const globalPopupCookie = CookieJar.getCookie(this.cookieNames.globalPopups);
		if (globalPopupCookie === 'true') {
			return;
		}

		if (!this.pageContext.GPOPUP || this.pageContext.GPOPUP.length === 0) {
			return;
		}

		CookieJar.setCookie(this.cookieNames.globalPopups, 'true');

		const {default: GlobalPopups} = await import('custom-extensions/global-popups');

		new GlobalPopups(this.pageContext);
	}

	async urlParameterPromotion () {
		if (this.pageContext.PGUQP?.length === 0) {
			return;
		}

		const url = new URL(window.location.href.toLowerCase());
		let paramName = null;
		let paramValue = null;

		for (const param of this.pageContext.PGUQP) {
			paramValue = url.searchParams.get(param.toLowerCase());

			if (paramValue === null) {
				continue;
			}

			paramName = param;
		}

		if (paramName === null || paramValue === null) {
			return;
		}

		const request = await fetch(`${this.pageContext.Japi_Url}_cachebust=${Date.now()}`, {
			body: JSON.stringify({
				function: 'pguqp',
				// eslint-disable-next-line camelcase
				param_name: paramName,
				// eslint-disable-next-line camelcase
				param_value: paramValue
			}),
			credentials: 'same-origin',
			headers: {
				'Cache-Control': 'no-cache, no-store, must-revalidate',
				Expires: 0,
				Pragma: 'no-cache'
			},
			method: 'POST'
		});

		if (!request.ok) {
			throw new Error('Network response was not successful');
		}

		const response = await request.json();

		if (!response || response.success !== 1) {
			return;
		}

		/* Send Event to GA */
		window.dataLayer = window.dataLayer || [];

		window.dataLayer.push({
			event: 'ga_event',
			// eslint-disable-next-line camelcase
			ga_event_action: 'Landed',
			// eslint-disable-next-line camelcase
			ga_event_category: 'URL Promotion',
			// eslint-disable-next-line camelcase
			ga_event_label: 'response.pricegroup_name',
			// eslint-disable-next-line camelcase
			ga_event_non_interaction: false
		});
	}

	async initCampaignLanding () {
		const campaignLandingElements = document.getElementsByClassName('js-campaign-landing');
		if (!campaignLandingElements.length) {
			return;
		}

		const {default: CampaignLanding} = await import('custom-extensions/campaign-landing');

		new CampaignLanding(this.pageContext);
	}

	async globalSlimBanners () {
		const banners = document.getElementsByClassName('js-global-slim-banner');

		if (!banners) {
			return;
		}

		const {default: GlobalSlimBanner} = await import('custom-extensions/global-slim-banner');

		for (const banner of banners) {
			new GlobalSlimBanner(banner);
		}
	}

	async initComponents () {
		// Stacked Slider
		const stackedSliders = document.getElementsByClassName('js-component-lp-stacked-slider');
		if (stackedSliders) {
			const {StackedSliders} = await import('custom-extensions/components');
			new StackedSliders(this.pageContext, stackedSliders);
		}

		// Specs Slider
		const specSliders = document.getElementsByClassName('js-component-lp-specs-slider');
		if (specSliders) {
			const {SpecsSlider} = await import('custom-extensions/components');
			for (const specSlider of specSliders) {
				new SpecsSlider(specSlider);
			}
		}

	}

	initKlevu () {
		if (this.pageContext?.Page_Code === 'SRCH') {
			return;
		}

		const searchInputs = document.querySelectorAll('#js-search-form-input-large, #js-search-form-input');
		if (!searchInputs.length) {
			return;
		}

		const loadKlevu2 = () => {
			const ts = new Date().getTime();
			const klevuScript = document.createElement('script');
			klevuScript.type = 'text/javascript';
			klevuScript.src = `https://js.klevu.com/theme/default/v2/quick-search.js?_=${ts}`;
			document.body.appendChild(klevuScript);
		};

		const loadKlevu = () => {
			for (const searchInput of searchInputs) {
				searchInput.removeEventListener('click', loadKlevu);
			}

			const ts = new Date().getTime();
			const klevuScript = document.createElement('script');
			klevuScript.type = 'text/javascript';
			klevuScript.src = `https://js.klevu.com/core/v2/klevu.js?_=${ts}`;
			klevuScript.onload = loadKlevu2;
			document.body.appendChild(klevuScript);
		};

		for (const searchInput of searchInputs) {
			searchInput.addEventListener('click', loadKlevu);
		}
	}

}
